import dayjs from '~/timeInit';

import type { ClaimCampaign, ClaimCampaignStatus } from '~/services/LuxLoyalty';
import type { Dayjs } from '~/services/TimeService';

const DISPLAY_FORMAT = 'DD/MM/YYYY HH:mm';

/** Value format of a datetime-local input */
export const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

/**
 * The earliest matchCheckUntil the API accepts: the latest book-by a member can get, i.e. enrolling on
 * the end date plus the book-by window. Null without an end date (matchCheckUntil then isn't allowed).
 */
export function earliestMatchCheckUntil(endDate: string, enrollmentValidityDays: number): Dayjs | null {
  if (!endDate || !Number.isFinite(enrollmentValidityDays)) {
    return null;
  }
  // The API adds whole UTC days (24h each). Adding local calendar days would be an hour off across a DST change.
  return dayjs(endDate).add(enrollmentValidityDays * 24, 'hour');
}

interface ValidateMatchCheckUntilInput {
  /** The form value (datetime-local), '' when empty */
  value: string;
  endDate: string;
  enrollmentValidityDays: number;
  /**
   * The saved matchCheckUntil when the campaign already has enrolments, otherwise null. Members past
   * book-by were promised that date, so it can't be cleared or moved earlier.
   */
  lockedFrom: string | null;
}

/** Mirrors the API's matchCheckUntil rules, so admins see them in the form rather than as a 400. */
export function validateMatchCheckUntil({
  value,
  endDate,
  enrollmentValidityDays,
  lockedFrom,
}: ValidateMatchCheckUntilInput): string | true {
  if (!value) {
    return lockedFrom ? "Can't be cleared once members have enrolled — they'd stop being checked" : true;
  }
  const earliest = earliestMatchCheckUntil(endDate, enrollmentValidityDays);
  if (!earliest) {
    return 'Set an end date first';
  }
  if (dayjs(value).isBefore(earliest, 'minute')) {
    return `Must be on or after ${earliest.format(DISPLAY_FORMAT)} (end date + book-by window)`;
  }
  if (lockedFrom && dayjs(value).isBefore(dayjs(lockedFrom), 'minute')) {
    return `Can't be moved earlier than ${dayjs(lockedFrom).format(DISPLAY_FORMAT)} once members have enrolled`;
  }
  return true;
}

/**
 * What to send as matchCheckUntil when updating a campaign: the new date when it changed, null to clear
 * an existing one, or undefined to leave it out.
 */
export function matchCheckUntilUpdate(
  formValue: string | undefined,
  existing: string | null | undefined,
): string | null | undefined {
  if (formValue) {
    return existing && dayjs(formValue).isSame(dayjs(existing), 'minute') ? undefined : formValue;
  }
  return existing ? null : undefined;
}

/**
 * Turns an API matchCheckUntil error into an inline message, with its ISO date shown locally, e.g.
 * "matchCheckUntil must not be before an enrolled member's book-by (2027-01-21T…)". Null for other errors.
 */
export function matchCheckUntilApiError(message: string | undefined): string | null {
  if (!message?.startsWith('matchCheckUntil')) {
    return null;
  }
  return message
    .replace(/^matchCheckUntil/, 'This date')
    .replace(/\(([^)]+)\)/, (match, iso: string) =>
      dayjs(iso).isValid() ? `(${dayjs(iso).format(DISPLAY_FORMAT)})` : match,
    );
}

/** matchCheckUntil as shown in the campaign dialog, e.g. "01/03/2031 10:59" */
export function formatMatchCheckUntil(iso: string): string {
  return dayjs(iso).format(DISPLAY_FORMAT);
}

const STATUSES_HIDING_CHALLENGE: ReadonlyArray<ClaimCampaignStatus> = ['EXPIRED', 'PAUSED'];

/**
 * Whether saving this status would hide a Status Challenge from customers while it's still checking them.
 * The Customer Portal only shows a challenge enrolment while the campaign is ACTIVE, but the cron keeps
 * checking (and upgrading) members until matchCheckUntil whatever the status, so moving an ACTIVE
 * challenge to EXPIRED or PAUSED early hides on-track members' progress without stopping their upgrade.
 */
export function shouldWarnStatusHidesChallenge(
  campaign: Pick<ClaimCampaign, 'type' | 'validatorType' | 'status' | 'matchCheckUntil'> | null | undefined,
  newStatus: ClaimCampaignStatus,
  now: Date = new Date(),
): boolean {
  if (campaign?.type !== 'MATCH' || campaign.validatorType !== 'ADMIN_ONLY') {
    return false;
  }
  if (!campaign.matchCheckUntil || !dayjs(campaign.matchCheckUntil).isAfter(now)) {
    return false;
  }
  return campaign.status === 'ACTIVE' && STATUSES_HIDING_CHALLENGE.includes(newStatus);
}
